import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  toast,
} from "@eduviaos/ui-kit";
import { AlertCircle, Loader2, Trash2 } from "lucide-react";
import { useDeleteEnquiry } from "@/hooks/useAdmissions";

/**
 * "Move to bin" confirmation. Soft-delete: the enquiry is hidden from
 * the default list but recoverable from the deleted-items view via
 * `POST /admissions/enquiries/{id}/restore`.
 *
 * We deliberately call this "Move to bin" instead of "Delete" in the
 * UI copy — it sets the right mental model (soft, reversible) rather
 * than the IA bracing for a permanent purge.
 *
 * Today the bin has no TTL — items live forever in the deleted set
 * until a Super Admin runs a purge job (Phase D8 — data retention).
 * The copy avoids quoting a specific recovery window so we don't
 * promise something the platform doesn't enforce.
 */

interface EnquiryDeleteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  enquiryId: string;
  studentName?: string;
  /** Fired after a successful 204. Caller usually navigates back to
   * the list (the detail route would 404 immediately since GET excludes
   * deleted by default). */
  onDeleted?: () => void;
}

export function EnquiryDeleteDialog({
  open,
  onOpenChange,
  enquiryId,
  studentName,
  onDeleted,
}: EnquiryDeleteDialogProps) {
  const del = useDeleteEnquiry();
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const onConfirm = async () => {
    setError(null);
    try {
      await del.mutateAsync({ enquiryId });
      toast.success(studentName ? `Moved ${studentName} to bin.` : "Moved enquiry to bin.");
      onOpenChange(false);
      onDeleted?.();
    } catch (err) {
      const e = err as { message?: string };
      setError(e?.message ?? "Couldn't move the enquiry to bin. Please try again.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-foreground flex items-center gap-2">
            <span className="bg-destructive/10 text-destructive inline-flex size-8 items-center justify-center rounded-md">
              <Trash2 className="size-4" aria-hidden="true" />
            </span>
            Move enquiry to bin
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            {studentName ? (
              <>
                <span className="text-foreground font-medium">{studentName}</span> will be hidden
                from your active funnel. You can restore them later from the bin.
              </>
            ) : (
              "This enquiry will be hidden from your active funnel. You can restore it later from the bin."
            )}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <p
            role="alert"
            className="text-destructive border-destructive/30 bg-destructive/5 mt-2 flex items-start gap-1.5 rounded-md border px-3 py-2 text-xs"
          >
            <AlertCircle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            <span>{error}</span>
          </p>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={del.isPending}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={del.isPending}
            data-testid="delete-confirm"
          >
            {del.isPending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden="true" />
            ) : (
              <Trash2 className="size-4" aria-hidden="true" />
            )}
            Move to bin
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
